import { useCallback, useEffect, useRef, useState } from "react";

export type DrawerMotionState = "closed" | "opening" | "open" | "closing";

export function useDrawerMotion(
  open: boolean,
  {
    openDuration = 200,
    closeDuration = 180,
    onAfterClose,
  }: {
    openDuration?: number;
    closeDuration?: number;
    onAfterClose?: () => void;
  } = {},
) {
  const motionTimer = useRef<number | null>(null);
  const afterCloseRef = useRef(onAfterClose);
  const [motionState, setMotionState] = useState<DrawerMotionState>(
    open ? "opening" : "closed",
  );
  afterCloseRef.current = onAfterClose;

  const clearPendingMotion = useCallback(() => {
    if (motionTimer.current !== null) {
      window.clearTimeout(motionTimer.current);
      motionTimer.current = null;
    }
  }, []);

  const finishClose = useCallback(() => {
    clearPendingMotion();
    setMotionState("closed");
    afterCloseRef.current?.();
  }, [clearPendingMotion]);

  useEffect(() => {
    clearPendingMotion();

    if (open) {
      setMotionState("opening");
      motionTimer.current = window.setTimeout(() => {
        motionTimer.current = null;
        setMotionState("open");
      }, openDuration);
      return clearPendingMotion;
    }

    setMotionState("closing");
    motionTimer.current = window.setTimeout(finishClose, closeDuration);
    return clearPendingMotion;
  }, [clearPendingMotion, closeDuration, finishClose, open, openDuration]);

  useEffect(() => clearPendingMotion, [clearPendingMotion]);

  return motionState;
}
